"use client"

import { useEffect, useRef, useState } from "react"
import { Users, ChevronDown, ChevronUp } from "lucide-react"
import { FamilyReactions, seenVerb } from "@/components/media/FamilyReactions"
import { OPEN_FAMILY_FEEDBACK_EVENT } from "@/components/media/MediaPageClient"
import { ReviewsSection } from "@/components/media/ReviewsSection"
import type { DashboardReview } from "@/lib/media-dashboard-data"

/**
 * Collapsible "Vous l'avez vu ?" section for the V3 dashboard, collapsed by
 * default to stay synthetic. Expands to per-member family reactions and, via a
 * toggle, the written avis families can read — reusing the existing
 * FamilyReactions and ReviewsSection so behaviour matches the classic fiche.
 */
export function DashboardFamilyFeedback({
  mediaId,
  mediaTitle,
  mediaType,
  reviews,
}: {
  mediaId: string
  mediaTitle: string
  mediaType: string
  reviews: DashboardReview[]
}) {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<"reactions" | "reviews">("reactions")
  const cardRef = useRef<HTMLDivElement>(null)
  const Chevron = open ? ChevronUp : ChevronDown

  // The top "Avis (N)" pill fires this event: open directly on the written
  // avis and bring the card into view — existing feedback used to be
  // invisible from the top (collapsed section + tab, two taps deep).
  useEffect(() => {
    const openFromTop = () => {
      setOpen(true)
      setTab("reviews")
      // Wait for the expanded content to render before measuring the scroll.
      requestAnimationFrame(() => {
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    }
    window.addEventListener(OPEN_FAMILY_FEEDBACK_EVENT, openFromTop)
    return () => window.removeEventListener(OPEN_FAMILY_FEEDBACK_EVENT, openFromTop)
  }, [])

  const seg = (t: "reactions" | "reviews", label: string) => {
    const active = tab === t
    return (
      <button
        type="button"
        onClick={() => setTab(t)}
        className="rounded-md px-3 py-1 text-[11px] font-semibold transition-colors"
        style={
          active
            ? { background: "var(--f-ink)", color: "var(--f-card)", boxShadow: "0 1px 2px rgba(42,37,31,.22)" }
            : { color: "var(--f-mid)", background: "transparent" }
        }
      >
        {label}
      </button>
    )
  }

  return (
    <div ref={cardRef} className="mb-[13px] scroll-mt-20">
      <div className="rounded-2xl" style={{ background: "var(--f-card)", border: "1px solid var(--f-border)" }}>
        <div className="flex items-center justify-between gap-3 p-4 sm:p-5">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
          >
            <span
              className="flex h-8 w-8 flex-none items-center justify-center rounded-lg"
              style={{ background: "var(--f-accent-soft)", color: "var(--f-accent)" }}
            >
              <Users className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span
                className="block font-serif text-[16px] font-medium"
                style={{ color: "var(--f-ink)", letterSpacing: "-.01em" }}
              >
                {seenVerb(mediaType).header}
              </span>
              <span className="block text-[11.5px]" style={{ color: "var(--f-muted)" }}>
                Réactions &amp; avis de votre famille{reviews.length > 0 ? ` · ${reviews.length} avis` : ""}
              </span>
            </span>
          </button>
          <div className="flex flex-none items-center gap-3">
            {open && (
              <div className="inline-flex items-center rounded-[10px] p-1" style={{ background: "var(--f-track)" }}>
                {seg("reactions", "Réactions")}
                {seg("reviews", `Avis écrits${reviews.length ? ` (${reviews.length})` : ""}`)}
              </div>
            )}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? "Réduire" : "Développer"}
              className="rounded-full p-1 transition-opacity hover:opacity-70"
            >
              <Chevron className="h-4 w-4" style={{ color: "var(--f-muted)" }} />
            </button>
          </div>
        </div>

        {open && (
          <div className="border-t px-4 pb-4 pt-4 sm:px-5" style={{ borderColor: "var(--f-divider)" }}>
            {tab === "reactions" ? (
              <FamilyReactions mediaId={mediaId} mediaTitle={mediaTitle} mediaType={mediaType} embedded />
            ) : (
              <ReviewsSection reviews={reviews} mediaId={mediaId} mediaTitle={mediaTitle} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
